// gym/GymClients.jsx (version avec gestion multi-devises)
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Alert, Snackbar, Chip, 
  FormControl, InputLabel, Select, MenuItem,
  Tooltip, Card, CardContent, Tabs, Tab,
  CircularProgress, InputAdornment, Avatar, Badge
} from '@mui/material';
import { 
  Add, Edit, Delete, Person, CheckCircle, 
  Cancel, Search, Refresh, Payment, AttachMoney,
  Phone, Email, CalendarToday, Euro, CurrencyExchange,
  Warning, Receipt, CalendarMonth, PersonAdd
} from '@mui/icons-material';
import api from '../../src/services/auth';

const GymClients = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [gymSettings, setGymSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    date_of_birth: '',
    gender: '',
    emergency_contact: '',
    medical_notes: '',
    is_active: true,
    // Champs pour les frais d'inscription
    registration_fee_paid: false,
    registration_fee_amount_htg: '0.00',
    registration_fee_amount_usd: '0.00',
    registration_payment_date: ''
  });

  const [paymentData, setPaymentData] = useState({
    currency_paid: 'HTG',
    amount_paid_htg: '0.00',
    amount_paid_usd: '0.00',
    payment_method: 'CASH',
    reference: '',
    notes: ''
  });

  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });

  const [stats, setStats] = useState({ 
    total: 0, 
    active: 0, 
    inactive: 0,
    paid_registration: 0,
    unpaid_registration: 0
  });

  const GENDER_CHOICES = [
    { value: 'M', label: 'Masculin' },
    { value: 'F', label: 'Féminin' }
  ];

  const PAYMENT_METHODS = [
    { value: 'CASH', label: 'Cash' },
    { value: 'CARD', label: 'Carte' },
    { value: 'MONCASH', label: 'MonCash' },
    { value: 'TRANSFER', label: 'Virement' }
  ];

  const CURRENCY_CHOICES = [
    { value: 'HTG', label: 'Gourdes (HTG)' },
    { value: 'USD', label: 'Dollars US (USD)' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterClients();
  }, [clients, searchTerm, statusFilter, paymentFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchGymSettings(), fetchClients()]);
    } catch (error) {
      console.error('Error fetching data:', error);
      showError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchGymSettings = async () => {
    try {
      setLoadingSettings(true);
      const response = await api.get('/gym/settings/');
      let settingsData;
      
      if (Array.isArray(response.data)) {
        settingsData = response.data[0];
      } else if (response.data.results) {
        settingsData = response.data.results[0];
      } else {
        settingsData = response.data;
      }
      
      setGymSettings(settingsData);
      console.log('Gym settings loaded:', settingsData);
    } catch (error) {
      console.error('Error fetching gym settings:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/gym/clients/');
      
      let clientsData = [];
      
      if (Array.isArray(response.data)) {
        clientsData = response.data;
      } else if (response.data && typeof response.data === 'object') {
        clientsData = response.data.results || Object.values(response.data);
      }
      
      setClients(clientsData);
      
      // Calculer les statistiques
      const total = clientsData.length;
      const active = clientsData.filter(c => c.is_active).length;
      const inactive = total - active;
      const paid_registration = clientsData.filter(c => c.registration_fee_paid).length;
      const unpaid_registration = total - paid_registration;
      
      setStats({ total, active, inactive, paid_registration, unpaid_registration });
    } catch (error) {
      console.error('Error fetching clients:', error);
      showError('Erreur lors du chargement des clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const filterClients = () => {
    const clientsArray = Array.isArray(clients) ? clients : [];
    
    let filtered = clientsArray;
    
    if (searchTerm) {
      filtered = filtered.filter(client =>
        (client.first_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (client.last_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (client.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (client.phone?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (client.code?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(client => 
        statusFilter === 'active' ? client.is_active : !client.is_active
      );
    }

    if (paymentFilter !== 'all') {
      filtered = filtered.filter(client => 
        paymentFilter === 'paid' ? client.registration_fee_paid : !client.registration_fee_paid
      );
    }
    
    setFilteredClients(filtered);
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        first_name: client.first_name || '',
        last_name: client.last_name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        date_of_birth: client.date_of_birth || '',
        gender: client.gender || '',
        emergency_contact: client.emergency_contact || '',
        medical_notes: client.medical_notes || '',
        is_active: client.is_active,
        registration_fee_paid: client.registration_fee_paid || false,
        registration_fee_amount_htg: client.registration_fee_amount_htg || '0.00',
        registration_fee_amount_usd: client.registration_fee_amount_usd || '0.00',
        registration_payment_date: client.registration_payment_date || ''
      });
    } else {
      setEditingClient(null);
      // Remplir les frais d'inscription depuis les paramètres
      const defaultHtg = gymSettings?.registration_fee_htg || '0.00';
      const defaultUsd = gymSettings?.registration_fee_usd || '0.00';
      
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        date_of_birth: '',
        gender: '',
        emergency_contact: '',
        medical_notes: '',
        is_active: true,
        registration_fee_paid: false,
        registration_fee_amount_htg: defaultHtg,
        registration_fee_amount_usd: defaultUsd,
        registration_payment_date: ''
      });
    }
    setOpenDialog(true);
  };

  const handleOpenPaymentDialog = (client) => {
    setSelectedClient(client);
    setPaymentData({
      currency_paid: 'HTG',
      amount_paid_htg: client.registration_fee_amount_htg || '0.00',
      amount_paid_usd: client.registration_fee_amount_usd || '0.00',
      payment_method: 'CASH',
      reference: '',
      notes: `Paiement frais d'inscription - ${client.first_name} ${client.last_name}`
    });
    setOpenPaymentDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClient(null);
    setSubmitting(false);
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    setSelectedClient(null);
    setProcessingPayment(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const apiData = {
        ...formData,
        registration_fee_amount_htg: parseFloat(formData.registration_fee_amount_htg) || 0,
        registration_fee_amount_usd: parseFloat(formData.registration_fee_amount_usd) || 0
      };

      if (editingClient) {
        await api.put(`/gym/clients/${editingClient.id}/`, apiData);
        showSuccess('Client modifié avec succès');
      } else {
        await api.post('/gym/clients/', apiData);
        showSuccess('Client créé avec succès');
      }
      
      fetchClients();
      handleCloseDialog();
    } catch (error) {
      console.error('Submit error:', error);
      showError('Erreur lors de l\'opération', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setProcessingPayment(true);

    try {
      // 1. Créer le paiement
      const paymentDataToSend = {
        client: selectedClient.id,
        payment_type: 'REGISTRATION',
        currency_paid: paymentData.currency_paid,
        amount_paid_htg: parseFloat(paymentData.amount_paid_htg) || 0,
        amount_paid_usd: parseFloat(paymentData.amount_paid_usd) || 0,
        exchange_rate_used: gymSettings?.exchange_rate_usd_to_htg || 130,
        payment_method: paymentData.payment_method,
        reference: paymentData.reference,
        notes: paymentData.notes
      };

      await api.post('/gym/payments/', paymentDataToSend);

      // 2. Mettre à jour le client pour marquer les frais comme payés
      const today = new Date().toISOString().split('T')[0];
      const clientUpdateData = {
        ...selectedClient,
        registration_fee_paid: true,
        registration_payment_date: today
      };

      await api.put(`/gym/clients/${selectedClient.id}/`, clientUpdateData);

      showSuccess('Paiement enregistré avec succès');
      fetchClients();
      handleClosePaymentDialog();
    } catch (error) {
      console.error('Payment error:', error);
      showError('Erreur lors du paiement', error);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce client?')) {
      try {
        await api.delete(`/gym/clients/${id}/`);
        showSuccess('Client supprimé avec succès');
        fetchClients();
      } catch (error) {
        showError('Erreur lors de la suppression');
      }
    }
  };

  const showSuccess = (message) => {
    setSnackbar({ open: true, message, severity: 'success' });
  };

  const showError = (message, error = null) => {
    let errorMessage = message;
    if (error?.response?.data) {
      const errors = Object.values(error.response.data).flat();
      errorMessage = errors.join(', ') || message;
    }
    setSnackbar({ open: true, message: errorMessage, severity: 'error' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR');
    } catch (e) {
      return 'N/A';
    }
  };

  const calculateAge = (dateString) => {
    if (!dateString) return null;
    try {
      const birthDate = new Date(dateString);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch (e) {
      return null;
    }
  };

  const formatCurrency = (amount, currency = 'HTG') => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleCurrencyChange = (currency) => {
    if (!gymSettings) return;
    
    const rate = parseFloat(gymSettings.exchange_rate_usd_to_htg) || 130;
    let newHtg = paymentData.amount_paid_htg;
    let newUsd = paymentData.amount_paid_usd;
    
    if (currency === 'USD') {
      // Convertir HTG en USD
      newUsd = (parseFloat(newHtg) / rate).toFixed(2);
    } else {
      // Convertir USD en HTG
      newHtg = (parseFloat(newUsd) * rate).toFixed(2);
    }
    
    setPaymentData({
      ...paymentData,
      currency_paid: currency,
      amount_paid_htg: newHtg,
      amount_paid_usd: newUsd
    });
  };

  const handleAmountChange = (field, value) => {
    const numericValue = parseFloat(value) || 0;
    
    if (field === 'amount_paid_htg') {
      setPaymentData({
        ...paymentData,
        amount_paid_htg: value,
        amount_paid_usd: (numericValue / (gymSettings?.exchange_rate_usd_to_htg || 130)).toFixed(2)
      });
    } else {
      setPaymentData({
        ...paymentData,
        amount_paid_usd: value,
        amount_paid_htg: (numericValue * (gymSettings?.exchange_rate_usd_to_htg || 130)).toFixed(2)
      });
    }
  };

  return (
    <Box p={3}>
      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Person sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">👥 Clients du GYM</Typography>
            <Typography variant="body2" color="textSecondary">
              Gestion des membres et frais d'inscription
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => handleOpenDialog()}
          disabled={loadingSettings}
        >
          Nouveau Membre
        </Button>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Person color="primary" />
                <Typography variant="h6">Total</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={2.4}>
          <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircle />
                <Typography variant="h6">Actifs</Typography>
              </Box>
              <Typography variant="h4">
                {stats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={2.4}>
          <Card sx={{ bgcolor: 'error.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Cancel />
                <Typography variant="h6">Inactifs</Typography>
              </Box>
              <Typography variant="h4">
                {stats.inactive}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={2.4}>
          <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Payment />
                <Typography variant="h6">Inscription Payée</Typography>
              </Box>
              <Typography variant="h4">
                {stats.paid_registration}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={2.4}>
          <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Warning />
                <Typography variant="h6">En attente</Typography>
              </Box>
              <Typography variant="h4">
                {stats.unpaid_registration}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Barre de recherche et filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Rechercher par nom, email, téléphone, code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="active">Actifs</MenuItem>
                <MenuItem value="inactive">Inactifs</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Frais d'inscription</InputLabel>
              <Select
                value={paymentFilter}
                label="Frais d'inscription"
                onChange={(e) => setPaymentFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="paid">Payés</MenuItem>
                <MenuItem value="unpaid">Non payés</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchData}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Actualiser'}
            </Button>
          </Grid>
          {gymSettings && (
            <Grid item xs={12} md={2}>
              <Chip
                icon={<CurrencyExchange />}
                label={`${gymSettings.primary_currency} (1 USD = ${gymSettings.exchange_rate_usd_to_htg} HTG)`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Tableau */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.light' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Code</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Membre</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Contact</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Âge</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Inscription</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Frais</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Chargement des clients...</Typography>
                </TableCell>
              </TableRow>
            ) : !Array.isArray(filteredClients) || filteredClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  {!Array.isArray(clients) || clients.length === 0 
                    ? 'Aucun client enregistré' 
                    : 'Aucun client trouvé avec les filtres actuels'}
                </TableCell>
              </TableRow>
            ) : (
              filteredClients.map((client) => (
                <TableRow key={client.id} hover>
                  <TableCell>
                    <Chip 
                      label={client.code || 'N/A'} 
                      color="primary" 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ bgcolor: client.gender === 'F' ? 'pink' : 'blue' }}>
                        {client.first_name?.[0]}{client.last_name?.[0]}
                      </Avatar>
                      <Box>
                        <Typography fontWeight="bold">
                          {client.first_name} {client.last_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {GENDER_CHOICES.find(g => g.value === client.gender)?.label || 'Non spécifié'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {client.email && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Email fontSize="small" color="action" />
                          <Typography variant="body2">{client.email}</Typography>
                        </Box>
                      )}
                      {client.phone && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Phone fontSize="small" color="action" />
                          <Typography variant="body2">{client.phone}</Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {client.date_of_birth ? (
                      <Tooltip title={`Né le ${formatDate(client.date_of_birth)}`}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CalendarToday fontSize="small" />
                          <span>{calculateAge(client.date_of_birth)} ans</span>
                        </Box>
                      </Tooltip>
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CalendarMonth fontSize="small" color="action" />
                        <Typography variant="body2">
                          {formatDate(client.joined_date)}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        Membre depuis {calculateAge(client.joined_date)} jours
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={client.is_active ? 'Actif' : 'Inactif'}
                      color={client.is_active ? 'success' : 'error'}
                      size="small"
                      icon={client.is_active ? <CheckCircle /> : <Cancel />}
                    />
                  </TableCell>
                  <TableCell>
                    {gymSettings?.require_registration_fee ? (
                      client.registration_fee_paid ? (
                        <Box>
                          <Chip
                            label="Payé"
                            color="success"
                            size="small"
                            icon={<CheckCircle />}
                          />
                          <Typography variant="caption" display="block">
                            {formatCurrency(client.registration_fee_amount_htg, 'HTG')}
                          </Typography>
                          {client.registration_fee_amount_usd > 0 && (
                            <Typography variant="caption" display="block" color="textSecondary">
                              {formatCurrency(client.registration_fee_amount_usd, 'USD')}
                            </Typography>
                          )}
                          {client.registration_payment_date && (
                            <Typography variant="caption" display="block" color="textSecondary">
                              Le {formatDate(client.registration_payment_date)}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Box>
                          <Chip
                            label="À payer"
                            color="warning"
                            size="small"
                            icon={<Warning />}
                          />
                          <Typography variant="caption" display="block">
                            {formatCurrency(client.registration_fee_amount_htg || gymSettings?.registration_fee_htg, 'HTG')}
                          </Typography>
                          <Typography variant="caption" display="block" color="textSecondary">
                            {formatCurrency(client.registration_fee_amount_usd || gymSettings?.registration_fee_usd, 'USD')}
                          </Typography>
                        </Box>
                      )
                    ) : (
                      <Chip
                        label="Non requis"
                        color="default"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {gymSettings?.require_registration_fee && !client.registration_fee_paid && (
                        <Tooltip title="Payer frais d'inscription">
                          <IconButton
                            color="warning"
                            size="small"
                            onClick={() => handleOpenPaymentDialog(client)}
                          >
                            <Payment />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Modifier">
                        <IconButton 
                          color="primary" 
                          size="small"
                          onClick={() => handleOpenDialog(client)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton 
                          color="error" 
                          size="small"
                          onClick={() => handleDelete(client.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pour créer/modifier un client */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingClient ? '✏️ Modifier le Membre' : '➕ Nouveau Membre'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prénom *"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nom *"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Téléphone *"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Adresse"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  multiline
                  rows={2}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date de naissance"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Genre</InputLabel>
                  <Select
                    value={formData.gender}
                    label="Genre"
                    onChange={(e) => setFormData({...formData, gender: e.target.value})}
                  >
                    <MenuItem value="">Non spécifié</MenuItem>
                    {GENDER_CHOICES.map(gender => (
                      <MenuItem key={gender.value} value={gender.value}>
                        {gender.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {gymSettings?.require_registration_fee && !editingClient && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Frais d'inscription
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Montant HTG"
                      value={formData.registration_fee_amount_htg}
                      onChange={(e) => setFormData({...formData, registration_fee_amount_htg: e.target.value})}
                      type="number"
                      margin="normal"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">HTG</InputAdornment>,
                      }}
                      inputProps={{ min: "0", step: "0.01" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Montant USD"
                      value={formData.registration_fee_amount_usd}
                      onChange={(e) => setFormData({...formData, registration_fee_amount_usd: e.target.value})}
                      type="number"
                      margin="normal"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: "0", step: "0.01" }}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact d'urgence"
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Statut du compte</InputLabel>
                  <Select
                    value={formData.is_active}
                    label="Statut du compte"
                    onChange={(e) => setFormData({...formData, is_active: e.target.value})}
                  >
                    <MenuItem value={true}>Actif</MenuItem>
                    <MenuItem value={false}>Inactif</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes médicales"
                  value={formData.medical_notes}
                  onChange={(e) => setFormData({...formData, medical_notes: e.target.value})}
                  multiline
                  rows={3}
                  margin="normal"
                  placeholder="Allergies, conditions médicales, restrictions..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={submitting}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} /> : editingClient ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog pour le paiement des frais d'inscription */}
      <Dialog open={openPaymentDialog} onClose={handleClosePaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Payment />
            <Typography>Paiement des frais d'inscription</Typography>
          </Box>
        </DialogTitle>
        {selectedClient && (
          <form onSubmit={handlePaymentSubmit}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Paiement pour {selectedClient.first_name} {selectedClient.last_name} ({selectedClient.code})
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Devise de paiement *</InputLabel>
                    <Select
                      value={paymentData.currency_paid}
                      label="Devise de paiement *"
                      onChange={(e) => handleCurrencyChange(e.target.value)}
                    >
                      {CURRENCY_CHOICES.map(currency => (
                        <MenuItem key={currency.value} value={currency.value}>
                          {currency.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {paymentData.currency_paid === 'HTG' ? (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Montant payé en HTG *"
                      value={paymentData.amount_paid_htg}
                      onChange={(e) => handleAmountChange('amount_paid_htg', e.target.value)}
                      type="number"
                      required
                      margin="normal"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">HTG</InputAdornment>,
                      }}
                      inputProps={{ min: "0.01", step: "0.01" }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      Équivalent : {formatCurrency(paymentData.amount_paid_usd, 'USD')}
                    </Typography>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Montant payé en USD *"
                      value={paymentData.amount_paid_usd}
                      onChange={(e) => handleAmountChange('amount_paid_usd', e.target.value)}
                      type="number"
                      required
                      margin="normal"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: "0.01", step: "0.01" }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      Équivalent : {formatCurrency(paymentData.amount_paid_htg, 'HTG')}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Méthode de paiement *</InputLabel>
                    <Select
                      value={paymentData.payment_method}
                      label="Méthode de paiement *"
                      onChange={(e) => setPaymentData({...paymentData, payment_method: e.target.value})}
                    >
                      {PAYMENT_METHODS.map(method => (
                        <MenuItem key={method.value} value={method.value}>
                          {method.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Référence (optionnel)"
                    value={paymentData.reference}
                    onChange={(e) => setPaymentData({...paymentData, reference: e.target.value})}
                    margin="normal"
                    placeholder="Numéro de transaction, référence bancaire..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes (optionnel)"
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                    multiline
                    rows={2}
                    margin="normal"
                  />
                </Grid>
                {gymSettings && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Taux de change appliqué : 1 USD = {gymSettings.exchange_rate_usd_to_htg} HTG
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClosePaymentDialog} disabled={processingPayment}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={processingPayment}
              >
                {processingPayment ? <CircularProgress size={24} /> : 'Confirmer le paiement'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GymClients;